import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { CalendlyProvider } from "@/components/site/CalendlyModal";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { CustomCursor } from "@/components/site/CustomCursor";
import { initTheme } from "@/components/site/ThemeSwitcher";
import { ScrollTop } from "@/components/site/ScrollTop";
import HomePage from "@/pages/HomePage";
import ServicePage from "@/pages/ServicePage";
import ProyectosPage from "@/pages/ProyectosPage";
import NosotrosPage from "@/pages/NosotrosPage";
import ContactoPage from "@/pages/ContactoPage";

const PageTransition = ({ children }) => (
  <motion.main
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.main>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => {
          window.__lenis ? window.__lenis.scrollTo(el, { offset: -40 }) : el.scrollIntoView({ behavior: "smooth" });
        }, 350);
        return;
      }
    }
    window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
        <Route path="/proyectos" element={<PageTransition><ProyectosPage /></PageTransition>} />
        <Route path="/nosotros" element={<PageTransition><NosotrosPage /></PageTransition>} />
        <Route path="/contacto" element={<PageTransition><ContactoPage /></PageTransition>} />
        <Route path="/:slug" element={<PageTransition><ServicePage /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  useEffect(() => {
    initTheme();
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <CalendlyProvider>
          <CustomCursor />
          <Navbar />
          <AnimatedRoutes />
          <Footer />
          <ScrollTop />
          <Toaster theme="dark" position="bottom-right" />
        </CalendlyProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
